import { useRef, useState, useEffect } from 'react'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useGetCampaign, useGetCampaignContent } from '../../../../queries'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setSelectedDevice } from '../../../../redux/Slices/deviceSlice'
import { Dialog } from 'primereact/dialog'
import { toggleDialog } from '../../../../redux/Slices/Global/globalSlice'
import LazyImage from '../../../SharedComponents/ShowMedia/LazyImage'
import { formatDuration } from '../../../../config/global'
import { ProgressBar } from 'primereact/progressbar'
import { Divider } from 'primereact/divider';

const IcDownload = () => <svg viewBox="0 0 24 24"><path d="M12 4v11m0 0 4-4m-4 4-4-4" /><path d="M4 18h16" /></svg>;
const IcClose = () => <svg width="18" height="18" viewBox="0 0 24 24" style={{ stroke: 'currentColor', fill: 'none', strokeWidth: 2 }}><path d="M6 6l12 12M18 6L6 18" /></svg>;

const DownloadingWaitingArea = ({ realtimeData }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch()

    const openDialog = useSelector((state) => state.global.openDialog);
    const [campaignId, setCampaignId] = useState(null);
    const [campaignName, setCampaignName] = useState(null);

    useEffect(() => {
        const newId = realtimeData?.download_percentage?.campaign_id;
        if (newId && newId !== campaignId) {
            setCampaignId(newId);
        }
        const name = realtimeData?.name;

        if (name === "Downloading..." && campaignData?.name) {
            setCampaignName(campaignData.name);
        } else if (name && name !== campaignName) {
            setCampaignName(name);
        }
    }, [realtimeData]);

    const [displayPercentage, setDisplayPercentage] = useState(1);
    const prevPercentageRef = useRef(null);

    useEffect(() => {
        const current = realtimeData?.download_percentage?.percentage;

        if (typeof current === 'number') {
            const previous = prevPercentageRef.current;

            // If current is 100
            if (current === 100) {
                // Show 100 only if previous is less than 100
                if (typeof previous === 'number' && previous < 100) {
                    setDisplayPercentage(100);
                } else {
                    setDisplayPercentage(1); // stale or repeated 100
                }
            } else {
                // Any other number, just show it
                setDisplayPercentage(current);
            }

            // Update previous
            prevPercentageRef.current = current;
        }
    }, [realtimeData?.download_percentage?.percentage]);

    const { isLoading, data } = useGetCampaignContent(campaignId, {
        enabled: !!campaignId,
    });

    const { data: campaignData } = useGetCampaign({
        id: campaignId,
        enabled: !!campaignId,
    });
    const onClose = () => {
        dispatch(setSelectedDevice({}));
        dispatch(toggleDialog("downloadProgress"))
    }

    useEffect(() => {
        if (campaignData) {
            setCampaignName(campaignData?.name || t('devices.group_downloading'));
        }
    }, [campaignData])

    const CardContent = ({ item }) => {
        return (
            <div className="flex items-center gap-3 w-full p-3 rounded-lg bg-gray-100 shadow-sm hover:bg-gray-200 transition-all">
                {/* Media Preview or Icon */}
                <div className="w-14 h-12 flex justify-center items-center rounded overflow-hidden bg-gray-100">
                    {item.media_type ? (
                        ["HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(item.media_type) ? (
                            <span className="text-xs text-center font-semibold text-white bg-gray-500 rounded px-2 py-1">
                                {(item.media_type).replace(/_/g, " ")}
                            </span>
                        ) : item.original_media_type === "WEATHER" ? (
                            <i className="fa-solid fa-cloud-sun text-xl text-gray-600" />
                        ) : item.original_media_type === "CLOCK" ? (
                            <i className="fa-solid fa-clock text-xl text-gray-600" />
                        ) : (
                            <LazyImage
                                media={item}
                                popupType="playlist_media_setting"
                                index={item.id}
                                styleClass="max-w-full max-h-full object-contain"
                            />
                        )
                    ) : (
                        <span className="text-xs font-semibold text-white bg-gray-400 rounded px-2 py-1">
                            {item.playlist_name ? t('devices.type_playlist') : t('devices.type_layout')}
                        </span>
                    )}
                </div>

                {/* Text Info */}
                <div className="flex-1">
                    <div className="text-sm font-medium text-gray-800 truncate">
                        {item.media_name ||
                            (item.playlist_name && `${t('devices.preview_playlist_label')} ${item.playlist_name}`) ||
                            `${t('devices.preview_layout_label')} ${item.layout_name}`}
                    </div>
                    <div className="text-xs text-gray-500">
                        {t('devices.assign_duration_label')}: {formatDuration(item.custom_duration)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Dialog
            visible={openDialog.downloadProgress}
            breakpoints={{ '960px': "95vw" }}
            style={{ width: '420px' }}
            showHeader={false}
            modal className="pp-dlg"
            onHide={onClose}>

            <div className="pp-dlg-hd">
                <span className="ic"><IcDownload /></span>
                <div>
                    <div className="t">{t('devices.group_download_progress_title')}</div>
                    <div className="s">{t('devices.group_campaign_label')} {campaignName}</div>
                </div>
                <span className="x" onClick={onClose}><IcClose /></span>
            </div>

            <div className="pp-dlg-bd">
                <div className="flex justify-between items-center text-sm text-muted mb-1">
                    <div className="flex gap-2 justify-center align-items-center">
                        <i className="fa-solid fa-spinner"></i>
                        <span>{t('devices.group_downloading')}</span></div>
                    <span>{displayPercentage}%</span>
                </div>
                <div id='downloadProgress' className="w-full rounded-full h-3 mb-4">
                    <ProgressBar className="shadow-md rounded-full h-3 bg-gray-200 progress-bar-animated"
                        value={displayPercentage}
                    ></ProgressBar>
                </div>
                <Divider />

                <div>
                    <div className="flex justify-start items-center text-sm text-muted mb-1 gap-2">
                        <i className="fa-solid fa-photo-film"></i>
                        <span>{t('devices.group_campaign_media')}</span>
                    </div>
                    <div className="h-[250px] overflow-auto scroll_div">
                        {isLoading ?
                            <div className="flex justify-content-center aligns-items-center">
                                <ProgressSpinner
                                    style={{ width: '30px', height: '30px' }}
                                    strokeWidth="3"
                                    animationDuration="2s"
                                />
                            </div>
                            :
                            data ?
                                Object.values(data).flat().map((item, index) => (
                                    <div key={index} className="overflow-hidden col-12 d-flex align-items-start p-1 mb-1 rounded hover:shadow-md">
                                        <CardContent item={item} />
                                    </div>
                                ))
                                :
                                null
                        }
                    </div>
                </div>
            </div>

            <div className="pp-dlg-ft">
                <button className="btn ghost" onClick={onClose}>{t('buttons.close')}</button>
            </div>
        </Dialog>
    )
}

export default DownloadingWaitingArea
